// Central company constants & shared marketing content for Triguna Solutions.
// Single source of truth used across the navbar, footer, and pages.

export const SITE = {
  name: "Triguna Solutions",
  shortName: "Triguna",
  email: "info@trigunasolutions.com",
  location: "Bartlesville, OK, USA",
  domain: "https://www.trigunasolutions.com",
  tagline: "Transform Ideas Into Intelligent Digital Solutions",
  description:
    "Triguna Solutions delivers Generative AI, mobile & web development, cloud engineering, and data engineering services for startups and enterprises across the USA.",
  social: {
    linkedin: "https://www.linkedin.com/company/triguna-solutions",
    twitter: "https://twitter.com/trigunasolutions",
    github: "https://github.com/trigunasolutions",
  },
  year: 2026,
};

// Hero highlights shown under the homepage headline
export const HERO_HIGHLIGHTS = [
  "AI-Driven Innovation",
  "Enterprise-Grade Development",
  "Cloud-Native Solutions",
  "Scalable Digital Products",
];

// Headline statistics (animated counters)
export const STATS = [
  { value: 150, suffix: "+", label: "Projects Delivered" },
  { value: 50, suffix: "+", label: "Enterprise Clients" },
  { value: 12, suffix: "+", label: "Years of Expertise" },
  { value: 98, suffix: "%", label: "Client Satisfaction" },
];

// "Why businesses choose Triguna"
export const WHY_CHOOSE = [
  {
    title: "AI-First Engineering Approach",
    description:
      "We embed intelligence into every layer of your product — from automation to decision support — so you ship smarter software.",
  },
  {
    title: "Experienced Technology Experts",
    description:
      "Senior engineers, architects, and data scientists with deep experience shipping production systems at scale.",
  },
  {
    title: "Agile Delivery Methodology",
    description:
      "Iterative, transparent sprints with continuous feedback loops that de-risk delivery and accelerate time to value.",
  },
  {
    title: "Enterprise Security Standards",
    description:
      "Security-by-design, compliance-aware architectures, and rigorous code review baked into our delivery process.",
  },
  {
    title: "Scalable Architecture Design",
    description:
      "Cloud-native, modular systems engineered to grow with your business without costly rewrites.",
  },
  {
    title: "Transparent Communication",
    description:
      "Clear roadmaps, shared dashboards, and proactive updates — you always know exactly where your project stands.",
  },
  {
    title: "Long-Term Partnership Focus",
    description:
      "We optimize for your outcomes, not billable hours — building relationships that compound in value over time.",
  },
];

// Industries served
export const INDUSTRIES = [
  { name: "Healthcare", icon: "HeartPulse", description: "HIPAA-aware platforms, clinical AI, and patient-centric digital experiences." },
  { name: "Finance", icon: "Landmark", description: "Secure, compliant fintech, analytics, and automation for modern finance." },
  { name: "Retail", icon: "ShoppingBag", description: "Omnichannel commerce, personalization, and real-time inventory intelligence." },
  { name: "Manufacturing", icon: "Factory", description: "Smart factory, IoT, and predictive maintenance solutions." },
  { name: "Logistics", icon: "Truck", description: "Route optimization, tracking, and supply-chain automation at scale." },
  { name: "Technology", icon: "Cpu", description: "Product engineering and platform scaling for fast-moving tech companies." },
  { name: "Education", icon: "GraduationCap", description: "Engaging learning platforms and data-driven student experiences." },
  { name: "Real Estate", icon: "Building2", description: "PropTech portals, virtual experiences, and workflow automation." },
];

// Technology partners shown in the credibility logo strip (trysol-style)
export const TECH_PARTNERS = [
  "AWS",
  "Microsoft Azure",
  "Google Cloud",
  "OpenAI",
  "Anthropic",
  "Databricks",
  "Snowflake",
  "Kubernetes",
  "NVIDIA",
  "MongoDB",
];

// Technology stack grouped by category
export const TECH_STACK = [
  { category: "Frontend", items: ["React", "Next.js", "Angular", "Vue"] },
  { category: "Backend", items: ["Node.js", "Python", "Java", ".NET"] },
  { category: "Mobile", items: ["Flutter", "React Native", "Swift", "Kotlin"] },
  { category: "Cloud", items: ["AWS", "Azure", "Google Cloud"] },
  { category: "AI", items: ["OpenAI", "Claude", "LangChain", "LlamaIndex", "Vector DBs"] },
  { category: "Data", items: ["Snowflake", "Databricks", "Spark", "Kafka"] },
];

// Delivery process steps
export const PROCESS = [
  { step: "Discovery", description: "We learn your goals, users, and constraints to define what success looks like." },
  { step: "Strategy", description: "We shape the roadmap, architecture, and tech choices that fit your business." },
  { step: "Design", description: "Human-centered UX and interface design validated with real workflows." },
  { step: "Development", description: "Agile, test-driven engineering with continuous integration and reviews." },
  { step: "Testing", description: "Automated and manual QA to ensure quality, performance, and security." },
  { step: "Deployment", description: "Zero-downtime releases with CI/CD pipelines and cloud infrastructure." },
  { step: "Support", description: "Ongoing monitoring, optimization, and iteration as your product grows." },
];

// Core values (About page)
export const CORE_VALUES = [
  { title: "Innovation", description: "We pursue better ways to solve problems with emerging technology.", icon: "Lightbulb" },
  { title: "Integrity", description: "We do what's right for our clients, even when no one is watching.", icon: "ShieldCheck" },
  { title: "Excellence", description: "We hold ourselves to the highest engineering and design standards.", icon: "Award" },
  { title: "Collaboration", description: "We work as one team with our clients toward shared outcomes.", icon: "Users" },
  { title: "Customer Success", description: "Your growth is the measure of our success.", icon: "TrendingUp" },
];

